import {
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import * as XLSX from "xlsx";
import {
  checkIfUserExistsAndJoined,
  checkUserExist,
  getUserDoc,
  userCollectionRef,
} from "../utils/firestoreHelpers";
import { createRandomId, findCodeBasedOnType } from "../utils/helpers";
import { fAuth, firebaseApp } from "../config/firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { getFunctions } from "firebase/functions";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { Loader } from "../hooks/useLoading";
import { requiredColumns } from "../utils/constants";

const UserTableModal = ({ onClose }) => {
  const [data, setData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  let emails = ["ashwinnambissan@apple.com"];

  const [
    sendWelcomeTemplateMsg,
    welcomeTemplateExecuting,
    welcomeTemplateError,
  ] = useHttpsCallable(getFunctions(firebaseApp), "sendWelcomeTemplate");

  const handleSendWelcomeTemplate = async (email, username, password) => {
    try {
      await sendWelcomeTemplateMsg({ username, email, password });
    } catch (error) {
      console.log("error :: ", error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    //  Allowed MIME types
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
    ];

    //  Validate file type
    if (!validTypes.includes(file.type)) {
      setErrors(["Only Excel files (.xlsx, .xls) are allowed"]);
      setData([]);
      e.target.value = ""; // reset input
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const binaryString = e.target.result;
      const workbook = XLSX.read(binaryString, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const excelData = XLSX.utils.sheet_to_json(worksheet);

      // Validate the data
      let temp = excelData.map((d, index) => {
        return {
          first_name: d?.first_name || "",
          last_name: d?.last_name || "",
          designation: d?.designation || "",
          company: d?.company || "",
          email: d?.email || "",
          dial_code: d?.dial_code || "",
          mobile_number: d?.mobile_number?.toString() || "",
          linkedIn: d?.linkedIn || "",
          status: "null",
          promo_code: d?.promo_code || "",
          android_redeem_code: d?.android_redeem_code || "",
          membership_tier: "Free"
        };
      });

      // console.log("send data for validation :: ", temp);
      const validationErrors = validateData(temp);

      if (validationErrors.length === 0) {
        setData([...temp]);
        setErrors([]);
      } else {
        setData([]);
        // setErrors([...validationErrors.join("\n")]);
        setErrors(validationErrors);
      }
    };

    reader.readAsBinaryString(file);
  };

  const validateData = (excelData) => {
    const errorArray = [];

    // Check if all required columns exist in the first row
    const firstRow = excelData[0];
    for (const columnName of requiredColumns) {
      if (!firstRow.hasOwnProperty(columnName)) {
        errorArray.push(`Column "${columnName}" is missing.`);
      }
    }

    excelData.forEach((row, index) => {
      const validationErrors = [];

      if (!row.first_name) {
        validationErrors.push("First Name is missing.");
      }
      if (!row.last_name) {
        validationErrors.push("Last Name is missing.");
      }
      // if (!row.designation) {
      //   validationErrors.push("Designation is missing.");
      // }
      // if (!row.company) {
      //   validationErrors.push("Company is missing.");
      // }
      // if (!row.android_redeem_code) {
      //   validationErrors.push("Android redeem code is missing.");
      // }
      // if (!row.promo_code) {
      //   validationErrors.push("Promo code is missing.");
      // }
      if (!row.email) {
        validationErrors.push("Email is missing.");
      } else if (!isValidEmail(row.email)) {
        validationErrors.push("Email has an invalid format.");
      }

      // if (!row.mobile_number || row.mobile_number.toString().length !== 10) {
      // if (!row.mobile_number) {
      //   validationErrors.push(
      //     `Mobile number is required`
      //   );
      // }

      if (row.mobile_number) {
        if (
          !row.dial_code ||
          findCodeBasedOnType("dial_code", row.dial_code).error
        ) {
          validationErrors.push("Invalid dial code");
        }
      }

      if (validationErrors.length > 0) {
        // errorArray.push(`Row ${index + 1} : ${validationErrors}`);
        errorArray.push(`Row ${index + 1}: ${validationErrors.join(", ")}`);
      }
    });

    setErrors(errorArray);
    return errorArray;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(email);
  };

  const saveUsersData = async () => {
    let error_found = false;
    try {
      setLoading(true);
      const temp = JSON.parse(JSON.stringify(data));

      for (let index = 0; index < temp.length; index++) {
        const item = temp[index];

        let password = createRandomId(10);

        const basePayload = {
          first_name: item?.first_name,
          last_name: item?.last_name,
          email: item?.email,
          designation: item?.designation || "",
          company: item?.company || "",
          linkedIn: item?.linkedIn || "",
          dial_code: item?.dial_code ? "+" + item?.dial_code : "",
          country_code: item?.dial_code ? findCodeBasedOnType("dial_code", item?.dial_code).data
            .code : "",
          mobile_number: item?.mobile_number ? item?.mobile_number : "",
          promo_code: item?.promo_code || "",
          android_redeem_code: item?.android_redeem_code || "",
          membership_tier:"Free"
        };

        const email = String(item?.email || "").trim();
        const existingSnap = await getDocs(
          query(userCollectionRef, where("email", "==", email))
        );
        const existingDoc = existingSnap?.docs?.length
          ? existingSnap.docs[0]
          : null;

        // If a user doc already exists for this email, update it.
        // Otherwise create a new doc with id=email to prevent duplicates.
        if (existingDoc) {
          try {
            const existingData = existingDoc.data ? existingDoc.data() : {};
            const wasDeleted = existingData?.deleted === true;
            const updatePayload = {
              ...basePayload,
              // do not overwrite password/new_user/status during bulk updates
            };
            if (wasDeleted) {
              updatePayload.deleted = false;
            }
            await updateDoc(existingDoc.ref, updatePayload);

            // Send invitation mail with password
            // await handleSendWelcomeTemplate(
            //   payload1.email,
            //   `${payload1.first_name} ${payload1.last_name}`,
            //   password
            // );

            temp[index].status = wasDeleted ? "Restored" : "Updated";
          } catch (updateUserError) {
            // console.error(
            //   `Row ${index + 1} - Error adding user:`,
            //   addUserError
            // );

            temp[index].status = "Failed";
            let error_found = true;
          }
        } else {
          // Create new
          try {
            const createPayload = {
              ...basePayload,
              status: "Created",
              new_user: true,
              password: password,
              created_at: serverTimestamp(),
            };
            await setDoc(getUserDoc(email), createPayload);
            temp[index].status = "Created";
          } catch (createUserError) {
            temp[index].status = "Failed";
            let error_found = true;
          }
        }
      }

      // Update the state with the modified data array
      setData([...temp]);
      if (error_found) {
        onClose();
      }
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={true} onHide={onClose} fullscreen>
        {loading && <Loader />}
        <Modal.Header closeButton>
          <Modal.Title>Upload Users data</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ width: "100%", paddingBottom: "30px" }}>
          <div>
            <div>
              <div style={{ paddingBottom: "20px" }}>
                <h4>Upload File</h4>
                <div className="d-flex justify-content-between">
                  <input
                    id="excelFile"
                    type="file"
                    label="Choose an Excel file"
                    onChange={handleFileChange}
                     accept=".xlsx, .xls"
                    disabled={loading}
                  />
                  <Button
                    variant="primary"
                    onClick={saveUsersData}
                    disabled={data.length === 0 || loading}
                  >
                    Save Users
                  </Button>
                </div>
                {/* {errors && <div className="text-danger">{errors}</div>} */}
                {/* {errors.length > 0 && (
                  <div className="text-danger">
                    {errors.map((err, i) => (
                      <div key={i}>{err}</div>
                    ))}
                  </div>
                )} */}
                {errors.length > 0 && (
                  <div className="text-danger">
                    {errors[0]}
                  </div>
                )}
              </div>
              {/* {errors?.map((err) => {
              err.map((e) => {
                return <span>{e}</span>;
              });
              return <span>{err}</span>;
            })} */}
            </div>
          </div>
          {data.length ? (
            <table className="table">
              <thead className="table-secondary">
                <tr>
                  <th className=""></th>
                  <th className="">First Name</th>
                  <th className="">Last Name</th>
                  <th className="">Designation</th>
                  <th className="">Email</th>
                  <th className="">Company</th>
                  <th className="">Dial code</th>
                  <th className="">Contact No</th>
                  <th className="">Status</th>
                </tr>
              </thead>
              <tbody>
                {data?.map((d, index) => {
                  // const d = d;
                  const { id } = d;

                  return (
                    <tr key={id}>
                      <td className="">{index + 1}</td>
                      <td className="">{d?.first_name}</td>
                      <td className="">{d?.last_name}</td>
                      <td className="">{d?.designation}</td>
                      <td className="">{d?.email}</td>
                      <td className="">{d?.company}</td>
                      <td className="">{d?.dial_code}</td>
                      <td className="">{d?.mobile_number}</td>
                      <td
                        className=""
                        style={{
                          color:
                            d?.status === "User already exists" ||
                            d?.status === "Failed"
                              ? "red"
                              : d?.status === "Success"
                              ? "green"
                              : "black",
                        }}
                      >
                        {/* {" "}
                      <button
                        className="btn btn-danger"
                        style={{ marginLeft: "auto" }}
                        onClick={() => {
                          //   setShowEditModal(true);
                          //   setSelectedId(d?.id);
                        }}
                      >
                        Delete
                      </button> */}
                        {d?.status}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </Modal.Body>
        {/* {data.length ? (
            <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>
                No
            </Button>
            <Button variant="danger" onClick={saveUsersData}>
                Yes
            </Button>
            </Modal.Footer>
        ) : null} */}
      </Modal>
    </>
  );
};

export default UserTableModal;
